/**
 * ChatWidget — Refatorado
 *
 * Recursos:
 * - FAB flutuante draggable (mouse + touch); posição persistida em localStorage
 * - Botão X pequeno no canto do FAB para fechar/remover (some até próximo reload ou toggle no menu)
 * - Não-admin vê o admin como "Suporte" (nome/role anônimos, sem info de online)
 * - Admin vê contador de THREADS distintas com msg não lida (não total de mensagens)
 * - Se última msg é do próprio user e sem resposta ≥ 30min → ring vermelho + timer "há X min sem resposta"
 * - Polling 5s
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, Circle, Clock, Volume2, VolumeX } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 2500;
const STORAGE_POS = 'chat_fab_pos';
const STORAGE_HIDDEN = 'chat_fab_hidden';

const fmtTime = (s) => { try { return new Date(s).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
const fmtLastSeen = (s) => {
  if (!s) return 'offline';
  try {
    const diff = Math.floor((Date.now() - new Date(s).getTime()) / 1000);
    if (diff < 120) return 'online agora';
    if (diff < 3600) return `${Math.floor(diff / 60)} min atrás`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
    return `${Math.floor(diff / 86400)}d atrás`;
  } catch { return 'offline'; }
};
const fmtWait = (min) => {
  if (min < 60) return `${min} min sem resposta`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}min sem resposta` : `${h}h sem resposta`;
};

export default function ChatWidget() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // FAB visibilidade (X esconde até próximo reload)
  const [fabHidden, setFabHidden] = useState(() => sessionStorage.getItem(STORAGE_HIDDEN) === '1');
  const [open, setOpen] = useState(false);

  // Posição do FAB (draggable). Default: bottom-right.
  const [pos, setPos] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_POS);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return { x: window.innerWidth - 80, y: window.innerHeight - 80 };
  });
  const dragState = useRef({ dragging: false, moved: false, offsetX: 0, offsetY: 0 });

  // Chat state
  const [threads, setThreads] = useState([]);
  const [contacts, setContacts] = useState([]);   // TODOS os contatos possíveis
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [online, setOnline] = useState([]);
  const [unreadThreads, setUnreadThreads] = useState(0);
  const scrollRef = useRef(null);
  const prevUnreadRef = useRef(0);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem('chat_sound') !== '0');

  // Toca "ding" curto usando WebAudio (não requer arquivo)
  const playDing = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.4);
    } catch { /* silent */ }
  }, [soundEnabled]);

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    localStorage.setItem('chat_sound', next ? '1' : '0');
  };

  // Expor abrir chat globalmente (para ícone da sidebar)
  useEffect(() => {
    window.openChat = () => { setOpen(true); setFabHidden(false); sessionStorage.removeItem(STORAGE_HIDDEN); };
    return () => { delete window.openChat; };
  }, []);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      axios.post(`${API}/chat/heartbeat`).catch(() => {});
      const [t, c] = await Promise.all([
        axios.get(`${API}/chat/threads`).catch(() => ({ data: [] })),
        axios.get(`${API}/chat/contacts`).catch(() => ({ data: [] })),
      ]);
      setThreads(t.data || []);
      setContacts(c.data || []);

      // Online = contatos com last_online nos últimos 2min
      const cutoff = Date.now() - 2 * 60 * 1000;
      const onlineList = (c.data || []).filter(u => {
        if (!u.last_online) return false;
        try { return new Date(u.last_online).getTime() >= cutoff; } catch { return false; }
      });
      setOnline(onlineList);

      if (isAdmin) {
        const s = await axios.get(`${API}/chat/admin/summary`).catch(() => ({ data: { unread_threads: 0 } }));
        const newUnread = s.data?.unread_threads || 0;
        if (newUnread > prevUnreadRef.current) playDing();
        prevUnreadRef.current = newUnread;
        setUnreadThreads(newUnread);
      } else {
        const newUnread = (t.data || []).reduce((s, x) => s + (x.unread || 0), 0);
        if (newUnread > prevUnreadRef.current) playDing();
        prevUnreadRef.current = newUnread;
        setUnreadThreads(newUnread);
      }

      if (selectedUserId) {
        const m = await axios.get(`${API}/chat/messages`, { params: { with_user_id: selectedUserId } });
        setMessages(m.data || []);
      }
    } catch { /* silent */ }
  }, [user, selectedUserId, isAdmin]);

  useEffect(() => {
    if (!user) return;
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [refresh, user]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (open && selectedUserId) {
      axios.post(`${API}/chat/messages/read`, { from_user_id: selectedUserId }).catch(() => {});
    }
  }, [open, selectedUserId, messages.length]);

  // Não-admin: auto-selecionar o Suporte (admin) assim que tivermos contatos/threads.
  // Sem isso, o corpo do chat ficava vazio (sem lista, sem conversa).
  useEffect(() => {
    if (isAdmin || selectedUserId) return;
    // 1º tenta thread existente; 2º pega o primeiro contato com role admin/suporte
    const tId = threads?.[0]?.with_user?.id;
    if (tId) { setSelectedUserId(tId); return; }
    const supp = (contacts || []).find(c => c.role === 'suporte' || c.role === 'admin');
    if (supp?.id) setSelectedUserId(supp.id);
  }, [isAdmin, selectedUserId, threads, contacts]);

  // ============ DRAG DO FAB ============
  const startDrag = (clientX, clientY) => {
    dragState.current = { dragging: true, moved: false, offsetX: clientX - pos.x, offsetY: clientY - pos.y };
  };
  const onMove = (clientX, clientY) => {
    if (!dragState.current.dragging) return;
    const nx = Math.max(8, Math.min(window.innerWidth - 64, clientX - dragState.current.offsetX));
    const ny = Math.max(8, Math.min(window.innerHeight - 64, clientY - dragState.current.offsetY));
    // Considera "moved" se deslocou > 5px do início
    const dx = Math.abs(clientX - (dragState.current.offsetX + pos.x));
    const dy = Math.abs(clientY - (dragState.current.offsetY + pos.y));
    if (dx > 5 || dy > 5) dragState.current.moved = true;
    setPos({ x: nx, y: ny });
  };
  const endDrag = () => {
    if (dragState.current.dragging) {
      dragState.current.dragging = false;
      try { localStorage.setItem(STORAGE_POS, JSON.stringify(pos)); } catch { /* ignore */ }
    }
  };

  useEffect(() => {
    const mm = (e) => onMove(e.clientX, e.clientY);
    const mu = () => endDrag();
    const tm = (e) => { if (e.touches?.[0]) onMove(e.touches[0].clientX, e.touches[0].clientY); };
    const te = () => endDrag();
    window.addEventListener('mousemove', mm);
    window.addEventListener('mouseup', mu);
    window.addEventListener('touchmove', tm, { passive: true });
    window.addEventListener('touchend', te);
    return () => {
      window.removeEventListener('mousemove', mm);
      window.removeEventListener('mouseup', mu);
      window.removeEventListener('touchmove', tm);
      window.removeEventListener('touchend', te);
    };
  }, [pos]);

  const send = async () => {
    if (!text.trim() || !selectedUserId) return;
    try {
      await axios.post(`${API}/chat/messages`, { to_user_id: selectedUserId, text: text.trim() });
      setText('');
      refresh();
    } catch { /* silent */ }
  };

  const hideFab = () => {
    setFabHidden(true);
    sessionStorage.setItem(STORAGE_HIDDEN, '1');
    setOpen(false);
  };

  if (!user) return null;

  // Thread atual (para calcular waiting)
  const currentThread = threads.find(t => t.with_user?.id === selectedUserId);
  const waitingMinutes = currentThread?.waiting_minutes || 0;
  const isWaiting = currentThread?.waiting;

  return (
    <>
      {/* Floating Button — DRAGGABLE */}
      {!open && !fabHidden && (
        <div
          className="fixed z-40"
          style={{ left: pos.x, top: pos.y }}
          data-testid="chat-fab-container"
        >
          <button
            onMouseDown={(e) => { e.preventDefault(); startDrag(e.clientX, e.clientY); }}
            onTouchStart={(e) => { if (e.touches?.[0]) startDrag(e.touches[0].clientX, e.touches[0].clientY); }}
            onClick={() => { if (!dragState.current.moved) setOpen(true); }}
            className={`relative w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-2xl hover:scale-105 active:scale-95 transition-transform flex items-center justify-center cursor-grab active:cursor-grabbing ${
              isWaiting ? 'ring-4 ring-red-500 ring-offset-2 animate-pulse' : ''
            }`}
            data-testid="chat-fab"
          >
            <MessageCircle className="w-6 h-6 pointer-events-none" />
            {unreadThreads > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[22px] h-[22px] flex items-center justify-center px-1 border-2 border-white pointer-events-none" data-testid="chat-fab-badge">
                {unreadThreads > 9 ? '9+' : unreadThreads}
              </span>
            )}
          </button>
          {/* X para esconder (canto oposto do badge) */}
          <button
            onClick={hideFab}
            className="absolute -top-1 -left-1 w-5 h-5 bg-gray-800 hover:bg-black text-white rounded-full flex items-center justify-center shadow-lg border-2 border-white"
            data-testid="chat-fab-close"
            title="Ocultar chat"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Drawer / Sidebar Chat */}
      {open && (
        <div
          className="fixed z-40 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[85vh] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden"
          style={{
            left: Math.min(pos.x, window.innerWidth - 400),
            top: Math.min(pos.y, window.innerHeight - 580),
          }}
          data-testid="chat-drawer"
        >
          <div className="px-3 py-2 bg-gradient-to-r from-blue-600 to-indigo-700 text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              <h3 className="font-bold text-sm" translate="no">
                {isAdmin ? 'Chat (Admin)' : 'Suporte'}
              </h3>
              {isWaiting && !isAdmin && (
                <span className="flex items-center gap-1 text-[10px] bg-red-500/90 px-2 py-0.5 rounded-full font-semibold">
                  <Clock className="w-3 h-3" /> {fmtWait(waitingMinutes)}
                </span>
              )}
            </div>
            <button onClick={toggleSound} className="p-1 hover:bg-white/20 rounded" data-testid="chat-sound-toggle" title={soundEnabled ? 'Silenciar' : 'Ativar som'}>
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/20 rounded" data-testid="chat-close-btn">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Admin: lista de threads à esquerda */}
          {isAdmin && !selectedUserId && (
            <div className="flex-1 overflow-y-auto p-2" data-testid="chat-threads-list">
              {online.length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Online agora (só admin vê)</p>
                  {online.map(u => (
                    <button key={u.id} onClick={() => setSelectedUserId(u.id)}
                      className="w-full flex items-center gap-2 px-2 py-2 hover:bg-gray-100 dark:hover:bg-gray-700/40 rounded-lg text-left"
                      data-testid={`chat-online-${u.id}`}>
                      <div className="relative">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm">
                          {(u.name || u.email || '?')[0].toUpperCase()}
                        </div>
                        <Circle className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 fill-green-500 text-green-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold truncate">{u.name || u.email}</p>
                        <p className="text-[10px] text-gray-500">{u.role}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">
                Conversas {unreadThreads > 0 && <span className="text-red-500 font-bold">({unreadThreads} pendentes)</span>}
              </p>
              {threads.length === 0 && online.length === 0 && (
                <p className="text-xs text-gray-500 text-center py-4">Nenhuma conversa ainda.</p>
              )}
              {threads.map(t => (
                <button key={t.with_user.id} onClick={() => setSelectedUserId(t.with_user.id)}
                  className={`w-full flex items-center gap-2 px-2 py-2 hover:bg-gray-100 dark:hover:bg-gray-700/40 rounded-lg text-left ${
                    t.unread > 0 ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
                  data-testid={`chat-thread-${t.with_user.id}`}>
                  <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-sm font-bold">
                    {(t.with_user.name || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold truncate">{t.with_user.name}</p>
                      {t.unread > 0 && <span className="bg-red-500 text-white text-[10px] px-1.5 rounded-full font-bold">{t.unread}</span>}
                    </div>
                    <p className="text-[10px] text-gray-500 truncate">{t.last_message?.text}</p>
                    <p className="text-[9px] text-gray-400 uppercase">{t.with_user.role}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Conversation view */}
          {selectedUserId && (
            <>
              <div className="flex items-center justify-between px-3 py-1 border-b border-gray-200 dark:border-gray-700">
                {isAdmin ? (
                  <button onClick={() => { setSelectedUserId(null); setMessages([]); }}
                    className="text-xs text-blue-600 hover:underline"
                    data-testid="chat-back-btn">
                    ← voltar
                  </button>
                ) : <span />}
                <p className="text-[10px] text-gray-500 truncate">
                  {(() => {
                    const c = contacts.find(x => x.id === selectedUserId);
                    if (!c) return '';
                    const isOn = online.some(o => o.id === selectedUserId);
                    const displayName = c.role === 'suporte' ? 'Suporte' : c.name;
                    return (
                      <>
                        <b>{displayName}</b> · {isOn ? <span className="text-green-600 font-semibold">online</span> : fmtLastSeen(c.last_online)}
                      </>
                    );
                  })()}
                </p>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50 dark:bg-gray-900/30" data-testid="chat-messages">
                {messages.length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-6">
                    {isAdmin ? 'Comece a conversa 👋' : 'Envie sua mensagem para o Suporte 👋'}
                  </p>
                ) : messages.map((m, i) => {
                  const mine = m.from_user_id === user.id;
                  return (
                    <div key={m.id || i} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${i}`}>
                      <div className={`max-w-[80%] px-3 py-1.5 rounded-2xl ${mine ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 rounded-bl-sm'}`}>
                        <p className="text-xs whitespace-pre-wrap break-words">{m.text}</p>
                        <p className={`text-[9px] mt-0.5 ${mine ? 'text-white/70' : 'text-gray-400'}`}>{fmtTime(m.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="p-2 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex gap-2">
                <input
                  type="text" value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
                  placeholder={isAdmin ? 'Responder...' : 'Digite sua mensagem para o Suporte...'}
                  className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="chat-input"
                />
                <button onClick={send} disabled={!text.trim()}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-3 rounded-lg flex items-center"
                  data-testid="chat-send-btn">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </>
          )}

          {/* Non-admin without selected (raro): mensagem */}
          {false && !isAdmin && !selectedUserId && (
            <div className="flex-1 flex items-center justify-center" data-testid="chat-loading">
              <p className="text-xs text-gray-500">Conectando ao Suporte...</p>
            </div>
          )}
        </div>
      )}
    </>
  );
}
